import * as ACTIONS from '../../store/actions/actions'
import Logger from '../../utils/Logger'

import { Controller, useForm } from 'react-hook-form'
import React, { useState, useEffect, useRef } from 'react'
import {
  addFileToUpload,
  convertToFormData,
  findParentWithFieldName,
  getFieldValue,
  getStepFields,
  getStepValidationSchema,
  getStepWithGroupName,
  getStepWithName,
  logDebug,
  renderFormElement,
  getFilteredSteps,
  stepsPav,
  stepsPecColl,
  stepsPecInd,
} from './helpers.js'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { BsFillQuestionCircleFill, BsSave, BsCheckCircle, BsXCircle } from 'react-icons/bs'
import { BtFormUpload } from '../../components/BtFormUpload'

import { Context } from '../../store/Store'
import { Oval } from 'react-loader-spinner'

import { PoRecapForm } from './PoRecapForm.js'
import ReactDatePicker from 'react-datepicker'
import Select from 'react-select'
import Stepper from '../../components/Stepper'
import axios from 'axios'
import { useDraftManager } from '../../hooks/useDraftManager'

var _ = require('lodash')

export function PoForm({ titleForm }) {
  // Créer un logger spécifique pour ce module
  const log = Logger.createModuleLogger('PoForm');

  // Flag pour activer/désactiver les dépôts de demandes d'aides
  const isSubmissionsEnabled = process.env.REACT_APP_PO_SUBMISSIONS_ENABLED === 'true'

  const { app, dispatch } = React.useContext(Context)


  const [loadingSubmit, setloadingSubmit] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  const [notification, setNotification] = useState(null)

  const { title, mesure } = useParams()

  const [currentStep, setCurrentStep] = useState(0)
  const [steps, setSteps] = useState()
  const [formDefaultValues, setFormDefaultValues] = useState(null)
  const [isLoadingDraft, setIsLoadingDraft] = useState(false)

  let navigate = useNavigate()
  const location = useLocation()

  // Rediriger si les soumissions sont désactivées
  useEffect(() => {
    if (!isSubmissionsEnabled) {
      navigate('/po', { replace: true })
    }
  }, [isSubmissionsEnabled, navigate])

  const searchParams = new URLSearchParams(location.search)
  const aidetype = searchParams.get('aidetype') || location.state?.aidetype
  const draftId = searchParams.get('draftId') || location.state?.draftId

  const { loadDraft, saveDraft, isLoading: isDraftLoading, error: draftError } = useDraftManager(app)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const queryParams = new URLSearchParams(window.location.search);
        const draftIdFromUrl = queryParams.get('draftId');
        const aidetypeFromUrl = queryParams.get('aidetype');
        const effectiveDraftId = draftIdFromUrl || (location.state?.draftId ? location.state.draftId : null);
        const effectiveAidetype = aidetypeFromUrl || (location.state?.aidetype ? location.state.aidetype : null);

        if (effectiveDraftId) {
          const draft = await loadDraft(effectiveDraftId)
          if (draft) {
            setFormDefaultValues(draft.form_data)
            dispatch(ACTIONS.setPoFormState(draft.form_data))
          }
        }

        if (!steps) {
          if (effectiveAidetype === 'Aides individuelles')
            setSteps(getFilteredSteps(stepsPecInd(app), mesure))
          else if (effectiveAidetype === 'Aides collectives')
            setSteps(getFilteredSteps(stepsPecColl(app), mesure))
          else if (effectiveAidetype === 'Aides à venir') setSteps(getFilteredSteps(stepsPav(app), mesure))
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error)
      }
    }

    fetchData()
  }, [location.pathname])


  // Référence pour suivre si le draft a déjà été chargé
  const draftLoadedRef = useRef(false);

  useEffect(() => {
    if (draftId && steps && steps.length > 0 && !draftLoadedRef.current) {
      draftLoadedRef.current = true; // Marquer le draft comme chargé
      loadDraftFromServer()
    }
  }, [draftId, steps])


  useEffect(() => {
    if (!steps || !steps[currentStep]) return;

    const searchParams = new URLSearchParams(window.location.search)
    const currentUrlStep = searchParams.get('step')

    if (currentUrlStep !== String(currentStep)) {
      searchParams.set('step', currentStep)
      const newUrl = `${window.location.pathname}?${searchParams.toString()}`
      window.history.replaceState(null, '', newUrl)
    }
  }, [currentStep, steps])

  const loadDraftFromServer = async () => {
    if (!draftId) return
    if (!steps || steps.length === 0) {
      log.error('Les étapes ne sont pas disponibles, impossible de charger le draft');
      return;
    }

    setIsLoadingDraft(true)
    try {
      const draftData = await loadDraft(draftId)
      log.debug('Draft chargé', draftData);

      if (draftData && draftData.form_data) {

        // Définir d'abord l'étape courante
        if (draftData.current_step !== undefined) {
          const stepIndex = parseInt(draftData.current_step)
          log.info(`Définition de l'étape courante: ${stepIndex}`);
          setCurrentStep(stepIndex)
        }

        // Nettoyer les données du formulaire
        const cleanedData = cleanDraftData(draftData.form_data)

        // Appliquer les données nettoyées au formulaire
        setFormDefaultValues(cleanedData)
        dispatch(ACTIONS.setPoFormState(cleanedData))

        // Recréer les champs dynamiques
        let updatedSteps = [...steps];

        // Trouver tous les champs dynamiques dans les données du draft
        const dynamicFields = [];
        const fileFields = [];

        // Parcourir les données du formulaire pour trouver les champs dynamiques
        Object.keys(cleanedData).forEach(groupKey => {
          if (typeof cleanedData[groupKey] === 'object' && cleanedData[groupKey] !== null) {
            Object.keys(cleanedData[groupKey]).forEach(fieldKey => {
              // Collecter tous les champs de type fichier pour débogage
              if (typeof cleanedData[groupKey][fieldKey] === 'object' &&
                  cleanedData[groupKey][fieldKey] &&
                  cleanedData[groupKey][fieldKey].name) {
                fileFields.push({ groupKey, fieldKey });
              }

              // Vérifier si c'est un champ dynamique avec timestamp (contient un tiret suivi de chiffres)
              const isDynamicField = /^[a-zA-Z0-9-]+-\d+$/.test(fieldKey);

              if (isDynamicField) {
                // Extraire le nom de base du champ (ex: 'releve-fichier' de 'releve-fichier-1750923358680')
                const baseFieldNameParts = fieldKey.split('-');
                const timestamp = baseFieldNameParts.pop(); // Retirer le timestamp
                const baseFieldName = baseFieldNameParts.join('-');

                dynamicFields.push({
                  groupKey,
                  fieldKey,
                  baseFieldName,
                  value: cleanedData[groupKey][fieldKey]
                });
              }
            });
          }
        });

        if (dynamicFields.length > 0) {
          log.info(`Premier champ dynamique: ${dynamicFields[0].fieldKey} dans groupe ${dynamicFields[0].groupKey}`);

          // Trouver tous les boutons d'ajout de fichier dans les étapes
          const addFileButtons = [];

          // Parcourir toutes les étapes pour trouver les boutons d'ajout de fichier
          updatedSteps.forEach((step, stepIndex) => {
            if (step.fields) {
              step.fields.forEach(field => {
                if (field.type === 'add-file') {
                  addFileButtons.push({
                    ...field,
                    stepIndex,
                    stepName: step.name
                  });
                }
              });
            }
          });

          if (addFileButtons.length > 0) {
            // Recréer les champs dynamiques
            dynamicFields.forEach(({ groupKey, fieldKey, baseFieldName, value }) => {
              // Trouver l'étape correspondante
              const stepIndex = updatedSteps.findIndex(step => step.groupName === groupKey);

              if (stepIndex !== -1) {
                const step = updatedSteps[stepIndex];

                // Vérifier si le champ existe déjà
                const fieldExists = step.fields.some(field => field.name === fieldKey);

                if (!fieldExists) {
                  // Trouver le bouton d'ajout de fichier dans cette étape
                  const addFileButton = step.fields.find(field =>
                    field.type === 'add-file');

                  if (addFileButton) {
                    const newSteps = addFileToUpload(updatedSteps, addFileButton.fieldName);

                    if (newSteps) {
                      updatedSteps = newSteps;

                      const lastStepIndex = updatedSteps.findIndex(step => step.groupName === groupKey);
                      if (lastStepIndex !== -1) {
                        const lastStep = updatedSteps[lastStepIndex];
                        const fileFields = lastStep.fields.filter(f => f.type === 'file');
                        const lastFileField = fileFields[fileFields.length - 1];

                        if (lastFileField) {
                          lastFileField.name = fieldKey;
                        } else {
                          const lastField = lastStep.fields.find(field => field.name && field.name.startsWith(baseFieldName + '-'));
                          if (lastField) {
                            lastField.name = fieldKey;
                          }
                        }
                      }
                    }
                  }
                }
              }
          })

          setSteps([...updatedSteps]);

          setTimeout(() => {
            setFormDefaultValues(cleanedData);
            dispatch(ACTIONS.setPoFormState(cleanedData));
          }, 100);
        }
      }}
    } catch (error) {
      log.error('Erreur lors du chargement du brouillon:', error);
      setNotification({
        type: 'error',
        message: 'Erreur lors du chargement du brouillon',
        timeout: 3000
      });
    } finally {
      setIsLoadingDraft(false);
    }
  }

  const cleanDraftData = (data) => {
    if (!data) return {}

    const cleanedData = JSON.parse(JSON.stringify(data))

    // Traiter tous les champs de fichiers pour s'assurer qu'ils ont les bonnes propriétés
    Object.keys(cleanedData).forEach(groupKey => {
      if (typeof cleanedData[groupKey] === 'object' && cleanedData[groupKey] !== null) {
        Object.keys(cleanedData[groupKey]).forEach(fieldKey => {
          const field = cleanedData[groupKey][fieldKey]

          // Si c'est un objet qui ressemble à un fichier
          if (field && typeof field === 'object' && (field.url || field.preview || field.name)) {
            // S'assurer que tous les champs nécessaires sont présents
            cleanedData[groupKey][fieldKey] = {
              ...field,
              isFile: true,
              isFileReference: true,
              preview: field.preview || field.url || '',
              url: field.url || field.preview || '',
              name: field.name || 'fichier',
              type: field.type || 'application/pdf',
              size: field.size || 0,
              fileId: field.fileId || `${groupKey}_${fieldKey}_${Date.now()}`
            }
          }
        })
      }
    })

    // Traitement des champs spéciaux comme dans l'ancien code
    Object.keys(cleanedData).forEach(key => {
      if (typeof cleanedData[key] === 'object' && cleanedData[key] !== null) {
        if (cleanedData[key][''] && typeof cleanedData[key][''] === 'object') {
          const fileInfo = cleanedData[key][''];

          if (fileInfo.fileId && typeof fileInfo.fileId === 'string') {
            const parts = fileInfo.fileId.split('_');
            if (parts.length >= 3) {
              const groupKey = parts[0];
              const fieldKey = parts[1];

              if (cleanedData[groupKey] && cleanedData[groupKey][fieldKey]) {
                if (!cleanedData[groupKey][fieldKey].url && fileInfo.url) {
                  cleanedData[groupKey][fieldKey].url = fileInfo.url;
                  cleanedData[groupKey][fieldKey].preview = fileInfo.preview || fileInfo.url;
                }
              }
            }
          }

          delete cleanedData[key][''];

          if (Object.keys(cleanedData[key]).length === 0) {
            delete cleanedData[key];
          }
        }
      }
    });

    // Nettoyer les objets vides
    Object.keys(cleanedData).forEach(key => {
      if (typeof cleanedData[key] === 'object' && cleanedData[key] !== null) {
        if (Object.keys(cleanedData[key]).length === 0) {
          delete cleanedData[key];
        }
      }
    });

    return cleanedData;
  }

  const {
    watch,
    register,
    getValues,
    trigger,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: formDefaultValues || {}
  })


  useEffect(() => {
    if (formDefaultValues && steps) {
      const filteredValues = filterFormValuesBySteps(formDefaultValues, steps)
      reset(filteredValues)
    }
  }, [formDefaultValues, steps, reset])


  const filterFormValuesBySteps = (values, steps) => {
    if (!values || !steps) return {}

    const filteredValues = {}

    if (values.A__mesure) filteredValues.A__mesure = values.A__mesure
    if (values.B__aidetype) filteredValues.B__aidetype = values.B__aidetype
    if (values.producteur) filteredValues.producteur = values.producteur

    steps.forEach(step => {
      const groupName = step.groupName
      if (values[groupName] && typeof values[groupName] === 'object') {
        filteredValues[groupName] = {}

        step.fields.forEach(field => {
          if (field.name && values[groupName][field.name] !== undefined) {
            filteredValues[groupName][field.name] = values[groupName][field.name]
          }
        })
      }
    })

    return filteredValues
  }

  const validateStep = async (stepIndex, data) => {
    if (!steps || !steps[stepIndex]) {
      return {};
    }

    const stepErrors = {}

    try {
      if (!Array.isArray(steps[stepIndex].fields)) {
        return {};
      }

      for (const field of steps[stepIndex].fields) {
        if (!field) {
          continue;
        }

        const { name, validation } = field

        if (!data || !data[steps[stepIndex].groupName]) {
          continue;
        }

        const dataValue = data[steps[stepIndex].groupName][name]

        if (!validation?.required) {
          continue;
        }

        if (
          name !== undefined &&
          ((validation?.required && !dataValue) ||
            dataValue === undefined ||
            dataValue === null)
        ) {
          stepErrors[`${steps[stepIndex].groupName}.${name}`] = validation?.required
          await trigger(`${steps[stepIndex].groupName}.${name}`)
        }
      }
    } catch (error) {
      console.error('Erreur lors de la validation de l\'étape:', error)
    }

    return stepErrors
  }

  const onSubmit = (data) => {
    setloadingSubmit(true)
    let dataFormated = convertToFormData(app.poFormState)

    // Si nous avons un brouillon chargé, inclure son ID dans la soumission
    // pour que l'API puisse le marquer comme soumis ou le supprimer
    if (draftId) {
      dataFormated.append('draft_id', draftId)
      console.log('Soumission finale du brouillon:', draftId)
    }

    dataFormated.append('producteur', app.profil.id)

    axios
      .post(
        `${
process.env.REACT_APP_APOLLO_URL || 'https://extranet.biobreizh.org'
        }/wp-json/bb/v1/po/`,
        dataFormated,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      .then(function (response) {
        if (response.status === 200) {
          setloadingSubmit(false)

          // Si nous avions un brouillon, le marquer comme soumis localement
          if (draftId) {
            console.log('Brouillon soumis avec succès:', draftId)
            // On pourrait éventuellement mettre à jour l'état local ou la liste des brouillons ici
          }

          if (
            aidetype === 'Aides collectives' ||
            aidetype === 'Aides individuelles'
          )
            navigate('/po/po-home-pec?success=create', { replace: true })
          else navigate('/po/po-home-pav?success=create', { replace: true })
        } else {
          setloadingSubmit(false)
        }
      })
      .catch(function (error) {
        setloadingSubmit(false)
        console.error('Erreur lors de la soumission:', error)
      })
  }

  const handleNextStep = async (data, stepIndex) => {
    const stepErrors = await validateStep(stepIndex, data)

    if (Object.keys(stepErrors).length === 0) {
      const nextStep = stepIndex + 1

      try {
        // D'abord mettre à jour l'état du formulaire
        dispatch(ACTIONS.setPoFormState(data))

        // Ensuite mettre à jour l'étape courante
        setCurrentStep(nextStep)

        // Enfin, mettre à jour l'URL sans déclencher de navigation
        // Utiliser setTimeout pour éviter les mises à jour en cascade
        setTimeout(() => {
          const urlParams = new URLSearchParams(window.location.search)
          if (aidetype) urlParams.set('aidetype', encodeURIComponent(aidetype))
          urlParams.set('step', nextStep)

          const newUrl = `${window.location.pathname}?${urlParams.toString()}`
          window.history.replaceState(null, '', newUrl)
        }, 0)
      } catch (error) {
        console.error('Erreur lors du passage à l\'étape suivante:', error)
      }
    }
  }

  const handleSaveDraft = async () => {
    const formData = watch()
    setSavingDraft(true)

    try {
      const preparedData = prepareFormDataForSave(formData)
      const formType = {
        aidetype,
        mesure,
        title: decodeURIComponent(title)
      }

      const savedDraftId = await saveDraft(preparedData, currentStep, formType, draftId)

      if (savedDraftId) {
        setNotification({
          type: 'success',
          message: 'Brouillon sauvegardé avec succès',
          timeout: 3000
        })

        if (!draftId) {
          navigate(`/po/po-form/${title}/${mesure}?aidetype=${encodeURIComponent(aidetype)}&draftId=${savedDraftId}`, {
            replace: true
          })
        }
      } else {
        setNotification({
          type: 'error',
          message: 'Erreur lors de la sauvegarde du brouillon',
          timeout: 3000
        })
      }
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Erreur lors de la sauvegarde du brouillon',
        timeout: 3000
      })
    } finally {
      setSavingDraft(false)
    }
  }


  const prepareFormDataForSave = (data) => {
    if (!data) return {}

    const preparedData = {}

    Object.keys(data).forEach(groupKey => {
      if (typeof data[groupKey] === 'object' && data[groupKey] !== null) {
        if (groupKey.startsWith('_') || groupKey === '') {
          return
        }

        preparedData[groupKey] = {}

        Object.keys(data[groupKey]).forEach(fieldKey => {
          const field = data[groupKey][fieldKey]

          if (fieldKey === '' || field === null || field === undefined) {
            return
          }

          if (field instanceof File) {
            preparedData[groupKey][fieldKey] = field
          } else {
            preparedData[groupKey][fieldKey] = field
          }
        })

        if (Object.keys(preparedData[groupKey]).length === 0) {
          delete preparedData[groupKey]
        }
      }
    })

    return preparedData
  }

  function getFieldElement(section, field, errors) {
    if (!field) return null;

    const { type, name, label, subLabel, validation, placeholder, options } = field;
    const { groupName, tooltip } = section || {}

    switch (type) {
      case 'hidden':
        return (
          <input
            type="hidden"
            name={name}
            id={name}
            value={app.profil.id}
            {...register(name)}
          />
        )
      case 'text':
        return (
          <div className="mb-4">
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor={name}
            >
              {label} <span className="text-red-500">*</span>
            </label>
            {errors[groupName]?.[name] && (
              <div className="text-xs text-red-700 mb-4">
                {errors[groupName]?.[name].message}
              </div>
            )}
            <input
              placeholder={placeholder}
              type="text"
              name={name}
              id={name}
              {...register(`${groupName}.${name}`, {
                ...validation,
              })}
              required={!!validation}
              className="block w-full shadow-sm focus:ring-base focus:border-base border-gray-300 rounded-md"
            />
          </div>
        )
      case 'date':
        return (
          <div className="mb-4">
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor={name}
            >
              {label} <span className="text-red-500">*</span>
            </label>

            {errors[groupName]?.[name] && (
              <div className="text-xs text-red-700 mb-4">
                {errors[groupName]?.[name].message}
              </div>
            )}

            <Controller
              control={control}
              name={`${groupName}.${name}`}
              rules={validation}
              render={({ field }) => {
                console.log(`DatePicker ${groupName}.${name} - valeur:`, field.value);

                // S'assurer que field.value est une date valide ou null
                let selectedDate = null;
                if (field.value) {
                  try {
                    selectedDate = field.value instanceof Date ? field.value : new Date(field.value);
                    // Vérifier si la date est valide
                    if (isNaN(selectedDate.getTime())) {
                      console.error(`Date invalide pour ${groupName}.${name}:`, field.value);
                      selectedDate = null;
                    }
                  } catch (error) {
                    console.error(`Erreur lors de la conversion de la date pour ${groupName}.${name}:`, error);
                    selectedDate = null;
                  }
                }

                return (
                  <ReactDatePicker
                    className="block w-full shadow-sm focus:ring-base focus:border-base border-gray-300 rounded-md"
                    placeholderText="Choisir une date"
                    locale="fr"
                    dateFormat="dd/MM/yyyy"
                    selected={selectedDate}
                    onChange={(date) => {
                      console.log(`DatePicker ${groupName}.${name} - onChange:`, date);
                      field.onChange(date);
                    }}
                    onBlur={field.onBlur}
                  />
                );
              }}
            />
          </div>
        )
      case 'select':
        return (
          <div className="mb-4">
            <label htmlFor={name}>{label}</label>

            {errors[groupName]?.[name] && (
              <div className="text-xs text-red-700 mb-4">
                {errors[groupName]?.[name].message}
              </div>
            )}

            <Controller
              control={control}
              name={`${groupName}.${name}`}
              id={name}
              {...register(`${groupName}.${name}`, { ...validation })}
              render={({ field }) => {
                return (
                  <Select
                    {...field}
                    options={options}
                    onChange={(selectedOption) =>
                      field.onChange(selectedOption.value)
                    }
                    value={options.find(
                      (option) => option.value === field.value
                    )}
                    placeholder="Votre choix ..."
                  />
                )
              }}
            />
          </div>
        )
      case 'file':
        return (
          <div className="mb-4 relative z-0">
            <label>
              {label}
              {validation && <span className=" text-red-700 mb-4">*</span>}
            </label>

            {tooltip && (
              <span
                className="absolute top-8 left-4 z-10 cursor-pointer"
                onClick={() => dispatch(ACTIONS.setTooltip(tooltip))}
              >
                <BsFillQuestionCircleFill color="green" />
              </span>
            )}

            <Controller
              name={`${groupName}.${name}`}
              control={control}
              rules={{ ...register(`${groupName}.${name}`, { ...validation }) }}
              render={({ field, fieldState }) => {
                return (
                  <div>
                    <BtFormUpload {...field} />
                    {fieldState.error && (
                      <p className="text-xs text-red-700 mb-4">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )
              }}
            />
          </div>
        )
      case 'add-file':
        return (
          <div
            onClick={() => {
              let newSteps = addFileToUpload(steps, field.fieldName)
              setSteps([...newSteps])
            }}
            className="cursor-pointer flex-1 items-center justify-center px-5 py-3 border border-transparent text-white font-medium rounded-md bg-green-700 hover:bg-green-900"
          >
            + Ajouter un autre scan ou photo
          </div>
        )
      default:
        return null
    }
  }

  const renderForm = () => {
    if (!steps || !Array.isArray(steps) || steps.length === 0) {
      return (
        <div className="p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded">
          Chargement des étapes en cours...
        </div>
      );
    }

    if (currentStep < 0 || currentStep >= steps.length) {
      return (
        <div className="p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded">
          Étape invalide. Veuillez réessayer.
        </div>
      );
    }

    try {
      const currentStepData = steps[currentStep];

      if (!currentStepData) {
        return (
          <div className="p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded">
            Impossible de charger cette étape.
          </div>
        );
      }

      return (
        <div>
          <h2 className="text-l font-bold tracking-tight text-gray-900 mt-6 mb-4 border-b-2 pb-2">
            {currentStepData.label || `Étape ${currentStep + 1}`}
          </h2>

          {currentStepData.subLabel && (
            <p className="text-sm text-red-600 mb-2">{currentStepData.subLabel}</p>
          )}

          <input type="hidden" {...register('A__mesure')} value={mesure} />
          <input type="hidden" {...register('B__aidetype')} value={aidetype} />

          {currentStepData.fields && Array.isArray(currentStepData.fields) ? (
            currentStepData.fields.map((field, fieldIndex) => (
              <div key={`field-${currentStep}-${fieldIndex}`}>
                {getFieldElement(
                  {
                    groupName: currentStepData.groupName
                  },
                  field,
                  errors
                )}
              </div>
            ))
          ) : (
            <div className="p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded">
              Aucun champ à afficher pour cette étape.
            </div>
          )}
        </div>
      );
    } catch (error) {
      console.error('Erreur dans renderForm:', error);
      return (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          Une erreur est survenue lors du chargement de cette étape.
        </div>
      );
    }
  }


  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null)
      }, notification.timeout || 3000)

      return () => clearTimeout(timer)
    }
  }, [notification])

  if (!steps || isLoadingDraft) return <div className="text-center py-10"><Oval wrapperStyle={{ justifyContent: 'center' }} className="m-auto justify-center" color="#0F5F3F" height={40} width={40} /> Chargement en cours...</div>
  else {
    return (
      <div className="bg-white p-6 mt-6 rounded-lg relative">
        {/* Notification */}
        {notification && (
          <div
            className={`absolute top-2 right-2 flex items-center p-3 rounded-md shadow-md ${notification.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
          >
            {notification.type === 'success' ? (
              <BsCheckCircle className="mr-2" />
            ) : (
              <BsXCircle className="mr-2" />
            )}
            {notification.message}
          </div>
        )}
        <h2
          className={`text-l font-bold tracking-tight text-gray-900 mt-4 mb-4 border-b-2 pb-2`}
        >
          {aidetype} - Etape {currentStep + 1}
          <br />
          <span className="text-xs">
            Mesure : {mesure} {decodeURIComponent(title)}
          </span>
        </h2>

        <Stepper
          steps={steps}
          setStep={(i) => setCurrentStep(i)}
          currentStep={currentStep}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-4 border-[1px] mb-4 border-gray-300">
            {/* {errors && <KError>{JSON.stringify(errors)}</KError>} */}
            {renderForm()}
            {currentStep === steps.length - 1 && (
              <PoRecapForm steps={steps} formData={{...app.poFormState, ...getValues()}} />
            )}
          </div>
          <div className="mt-4 flex justify-center">
            <div className="my-5 flex justify-start sm:space-y-0 gap-4">
              {currentStep + 1 > steps.length - 1 ? (
                <>
                  {!loadingSubmit ? (
                    <button className="inline-flex items-center px-6 py-3 border border-transparent font-medium rounded-full shadow-sm text-white bg-base hover:bg-base focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-base">
                      Finaliser
                    </button>
                  ) : (
                    <div className="text-center">
                      <Oval
                        wrapperStyle={{ justifyContent: 'center' }}
                        className="m-auto justify-center"
                        color="#0F5F3F"
                        height={40}
                        width={40}
                      />{' '}
                      Veuillez patienter... l'envoi peut être assez long
                    </div>
                  )}
                </>
              ) : (
                <>
                  <span
                    onClick={() => {
                      handleNextStep(watch(), currentStep)
                    }}
                    className="cursor-pointer inline-flex items-center px-6 py-3 border border-transparent font-medium rounded-full shadow-sm text-white bg-base hover:bg-base focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-base"
                  >
                    Continuer
                  </span>
                  <span
                    onClick={handleSaveDraft}
                    className="cursor-pointer inline-flex items-center px-6 py-3 border border-gray-300 font-medium rounded-full shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    {savingDraft ? (
                      <>
                        <Oval
                          wrapperStyle={{ justifyContent: 'center' }}
                          className="mr-2"
                          color="#4B5563"
                          height={20}
                          width={20}
                        />
                        Sauvegarde...
                      </>
                    ) : (
                      <>
                        <BsSave className="mr-2" />
                        Sauvegarder et continuer plus tard
                      </>
                    )}
                  </span>
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    )
  }
}
